/**
 * Geometry for the Striping Plan map tab (spec 2026-10-14 Parts A-B): feet on the
 * sphere the striping service uses (Backend/Services/Linear_report/utils/
 * intersections.py EARTH_RADIUS_FT) and a point's nearest place on a polyline.
 * Points are {lat, lng}; polylines are arrays of them.
 */
export const EARTH_RADIUS_FT = 6371008.8 * 3.280839895;
const RAD = Math.PI / 180;

export const distanceFt = (a, b) => {
  const dLat = (b.lat - a.lat) * RAD;
  const dLng = (b.lng - a.lng) * RAD;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_FT * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * {distanceFt, alongFt}: the point's distance to the polyline and how far along the
 * line (from its first vertex) the nearest place lies; null for fewer than 2 points.
 * Works in a flat projection centred on the point — exact enough at road scale.
 */
export const nearestOnLine = (point, line) => {
  if (!Array.isArray(line) || line.length < 2) return null;
  const cosLat = Math.cos(point.lat * RAD);
  const toXY = (p) => ({
    x: (p.lng - point.lng) * RAD * cosLat * EARTH_RADIUS_FT,
    y: (p.lat - point.lat) * RAD * EARTH_RADIUS_FT,
  });
  let best = null;
  let walked = 0;
  for (let i = 0; i < line.length - 1; i += 1) {
    const a = toXY(line[i]);
    const b = toXY(line[i + 1]);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, -(a.x * dx + a.y * dy) / len2)) : 0;
    const d = Math.hypot(a.x + t * dx, a.y + t * dy);
    const segLen = Math.sqrt(len2);
    if (!best || d < best.distanceFt) best = { distanceFt: d, alongFt: walked + t * segLen };
    walked += segLen;
  }
  return best;
};

export const formatCoord = (value) => Number(value).toFixed(5);
